/**
 * Allys CTA Section
 * 
 * Modern horizontal card with logo mask effect
 * Features frangipani flower imagery through logo cutout
 */

'use client';

import Link from 'next/link';
import { useEffect, useState } from 'react';
import { analyticsEvents } from '@/components/analytics/GoogleAnalytics';

export interface AllysCTAButton {
  text: string;
  href: string;
  variant?: 'primary' | 'secondary' | 'outline';
  external?: boolean;
}

export interface AllysCTASectionProps {
  /**
   * Main heading text
   */
  title?: string;
  
  /**
   * Description text
   */
  description?: string;
  
  /**
   * Primary CTA button
   */
  primaryButton?: AllysCTAButton;
  
  /**
   * Secondary CTA button
   */
  secondaryButton?: AllysCTAButton;
  
  /**
   * Background image for logo mask (single image)
   * @default Frangipani flowers
   */
  maskBackgroundImage?: string;
  
  /**
   * Array of background images for animated rotation
   * If provided, overrides maskBackgroundImage
   */
  maskBackgroundImages?: string[];
  
  /**
   * Logo SVG URL for mask
   * @default Allys logo
   */
  logoMaskUrl?: string;
  
  /**
   * Interval in milliseconds for image rotation
   * @default 9000
   */
  rotationInterval?: number;
  
  /**
   * Card background color
   * @default 'white'
   */
  cardBackground?: 'white' | 'gradient' | 'primary';
  
  /**
   * Messages for translations
   */
  messages?: Record<string, unknown>;
  
  /**
   * Current locale
   */
  locale?: 'en' | 'es' | 'fr';
}

// Helper to safely get string from messages
const safeGetString = (obj: unknown, defaultValue: string): string => {
  if (typeof obj === 'string') return obj;
  return defaultValue;
};

/**
 * Allys CTA Section Component
 * 
 * Modern horizontal card with logo mask revealing background image
 */
export function AllysCTASection({
  title,
  description,
  primaryButton,
  secondaryButton,
  maskBackgroundImage = 'https://media.routeroyal.com/GdS1cGKCX71Gk6hIKB_5c2EQds5fD3AfXKQLzIkMugA/raw:1/czM6Ly9jb2xsYXBzaWJsZS1wdXJzZS1kbW9rLWRubDd4cy9yb3V0ZXJveWFsL3JveWFsZS9zdGF0aWMvY2MwOTUxYzQ4ZjdiYmYwMWMzYWM5MjdlNjcyYzEyYTk4NGMwYWRjMDhlMmU1ZWYzMjUyYzA4ZDVmNzY0YzhjYy53ZWJw.webp', // White and yellow frangipani flowers
  maskBackgroundImages,
  logoMaskUrl = '/media/royale/92d022e91884367af9171c55a4d87a40daf1fcf9572a7b1b3746354c999cbceb.svg',
  cardBackground = 'white',
  rotationInterval = 9000,
  messages,
  locale,
}: AllysCTASectionProps) {
  // Get translations from messages
  const ctaMsg = messages?.cta as Record<string, unknown> | undefined;
  const displayTitle = title || safeGetString(ctaMsg?.title, 'Ready to Find Your Dream Property?');
  const displayDescription = description || safeGetString(ctaMsg?.description, 'Join thousands of satisfied customers and let us help you find the perfect home.');
  const primaryButtonText = primaryButton?.text || safeGetString(ctaMsg?.primaryButton, 'Find a Property');
  const secondaryButtonText = secondaryButton?.text || safeGetString(ctaMsg?.secondaryButton, 'Contact Us');
  
  const resolvedPrimaryButton = primaryButton ?? {
    text: primaryButtonText,
    href: safeGetString(ctaMsg?.primaryButtonHref, '/properties'),
  };
  const resolvedSecondaryButton = secondaryButton ?? {
    text: secondaryButtonText,
    href: safeGetString(ctaMsg?.secondaryButtonHref, '/contact'),
  };

  const localePrefix = locale ? `/${locale}` : '';
  const resolveHref = (href: string) => {
    if (href.startsWith('http') || href.startsWith('mailto:') || href.startsWith('tel:')) return href;
    if (locale && !href.startsWith(`/${locale}`)) return `${localePrefix}${href}`;
    return href;
  };
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [nextImageIndex, setNextImageIndex] = useState(1);
  const [opacity, setOpacity] = useState(1);
  
  // Use the array if provided, otherwise use single image
  const images = maskBackgroundImages || [maskBackgroundImage];
  const currentImage = images[currentImageIndex];
  const nextImage = images[nextImageIndex];
  
  // Set up image rotation with crossfade
  useEffect(() => {
    if (images.length <= 1) return; // Don't rotate if only one image
    
    const interval = setInterval(() => {
      // Start fading to next image
      setOpacity(0);
      
      setTimeout(() => {
        // Switch to next image and reset opacity
        setCurrentImageIndex(nextImageIndex);
        setNextImageIndex((nextImageIndex + 1) % images.length);
        setOpacity(1);
      }, 1000); // 1 second transition duration
      
    }, rotationInterval);
    
    return () => clearInterval(interval);
  }, [images.length, rotationInterval, nextImageIndex]);
  
  const bgClass = {
    white: 'bg-white',
    gradient: 'bg-gradient-to-br from-[var(--color-primary)]/5 to-[var(--color-accent)]/5',
    primary: 'bg-[var(--color-primary)] text-white',
  }[cardBackground];

  return (
    <section className="relative py-16 lg:py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Horizontal Card */}
        <div className={`relative rounded-3xl overflow-hidden shadow-2xl ${bgClass}`}>
          <div className="relative z-10 flex flex-col lg:flex-row items-center">
            {/* Left Side - Content */}
            <div className="w-full lg:w-1/2 p-8 sm:p-12 lg:p-20 relative">
              {/* Decorative accent - Gold */}
              <div className="absolute top-8 left-8 w-20 h-1.5 bg-linear-to-r from-[#BBA339] to-[#E4CE4A] rounded-full shadow-lg shadow-[#BBA339]/20" />
              
              <div className="relative">
                <h2 
                  className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-gray-900 mb-6 tracking-tight leading-[1.1] font-inter"
                >
                  {displayTitle}
                </h2>
                
                {displayDescription && (
                  <p 
                    className="text-lg lg:text-xl text-gray-600 mb-10 leading-relaxed max-w-2xl font-light font-inter"
                  >
                    {displayDescription}
                  </p>
                )}

                {/* CTA Buttons */}
                <div className="flex flex-col sm:flex-row gap-4 items-start">
                  {resolvedPrimaryButton.href && (
                    <Link
                      href={resolveHref(resolvedPrimaryButton.href)}
                      target={resolvedPrimaryButton.external ? '_blank' : undefined}
                      rel={resolvedPrimaryButton.external ? 'noopener noreferrer' : undefined}
                      onClick={() => analyticsEvents.custom('cta_click', { cta_text: primaryButtonText, cta_location: 'cta_section', cta_destination: resolvedPrimaryButton.href, cta_variant: 'primary' })}
                      className="group relative inline-flex items-center justify-center gap-2.5 px-6 py-3 bg-[#0D5C35] hover:bg-[#094826] text-white font-semibold rounded-full transition-all duration-300 transform hover:scale-105 shadow-xl hover:shadow-2xl overflow-hidden whitespace-nowrap font-inter"
                    >
                      <svg className="w-5 h-5 relative z-10 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      <span className="relative z-10 font-bold">{primaryButtonText}</span>
                      <div className="absolute inset-0 bg-linear-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                    </Link>
                  )}
                  
                  {resolvedSecondaryButton.href && (
                    <Link
                      href={resolveHref(resolvedSecondaryButton.href)}
                      target={resolvedSecondaryButton.external ? '_blank' : undefined}
                      rel={resolvedSecondaryButton.external ? 'noopener noreferrer' : undefined}
                      onClick={() => analyticsEvents.custom('cta_click', { cta_text: secondaryButtonText, cta_location: 'cta_section', cta_destination: resolvedSecondaryButton.href, cta_variant: 'secondary' })}
                      className="group relative inline-flex items-center justify-center gap-2.5 px-6 py-3 bg-white border-2 border-(--color-primary) text-(--color-primary) hover:bg-(--color-primary) hover:text-white font-semibold rounded-full transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl overflow-hidden whitespace-nowrap font-inter"
                    >
                      <svg className="w-5 h-5 relative z-10 shrink-0 transition-transform duration-300 group-hover:rotate-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                      </svg>
                      <span className="relative z-10 font-bold">{secondaryButtonText}</span>
                      <div className="absolute inset-0 bg-(--color-primary) opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    </Link>
                  )}
                </div>
              </div>
            </div>

            {/* Right Side - Logo Mask with Floral Image Inside */}
            <div className="w-full lg:w-1/2 h-80 lg:h-[500px] relative overflow-hidden flex items-center justify-center">
              <svg 
                className="w-full h-full max-w-3xl max-h-full p-4 lg:p-6" 
                viewBox="0 0 500 500"
                preserveAspectRatio="xMidYMid meet"
                xmlns="http://www.w3.org/2000/svg"
              >
                <defs>
                  {/* Define the current background image as a pattern */}
                  <pattern 
                    id="floral-bg-pattern-current" 
                    patternUnits="objectBoundingBox" 
                    width="1" 
                    height="1"
                  >
                    <image 
                      href={currentImage}
                      x="0" 
                      y="0" 
                      width="500" 
                      height="500"
                      preserveAspectRatio="xMidYMid meet"
                    />
                  </pattern>
                  
                  {/* Define the next background image as a pattern */}
                  <pattern 
                    id="floral-bg-pattern-next" 
                    patternUnits="objectBoundingBox" 
                    width="1" 
                    height="1"
                  >
                    <image 
                      href={nextImage}
                      x="0" 
                      y="0" 
                      width="500" 
                      height="500"
                      preserveAspectRatio="xMidYMid meet"
                    />
                  </pattern>
                  
                  {/* Load the logo SVG as a mask */}
                  <mask id="logo-shape-mask">
                    {/* Black rectangle as base - this will hide everything */}
                    <rect x="0" y="0" width="500" height="500" fill="black"/>
                    {/* Load logo in white to show only this shape */}
                    <image 
                      href={logoMaskUrl}
                      x="0" 
                      y="0" 
                      width="500" 
                      height="500"
                      preserveAspectRatio="xMidYMid meet"
                      style={{ filter: 'brightness(0) invert(1) contrast(100)' }}
                    />
                  </mask>
                </defs>
                
                {/* Layer 1: Next image (bottom layer) */}
                <rect 
                  x="0" 
                  y="0" 
                  width="500" 
                  height="500" 
                  fill="url(#floral-bg-pattern-next)"
                  mask="url(#logo-shape-mask)"
                  style={{
                    filter: 'brightness(1.1) contrast(1.2) saturate(1.4)',
                  }}
                />
                
                {/* Layer 2: Current image (top layer with fade) */}
                <rect 
                  x="0" 
                  y="0" 
                  width="500" 
                  height="500" 
                  fill="url(#floral-bg-pattern-current)"
                  mask="url(#logo-shape-mask)"
                  style={{
                    filter: 'brightness(1.1) contrast(1.2) saturate(1.4)',
                    opacity: opacity,
                    transition: 'opacity 1s ease-in-out',
                  }}
                />
              </svg>
              
              {/* Subtle shadow overlay for depth */}
              <div 
                className="absolute inset-0 pointer-events-none opacity-5"
                style={{
                  boxShadow: 'inset 0 0 60px rgba(0,0,0,0.1)',
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
